import React, { useCallback, useContext, useState } from 'react';
import { Button, Loader, Modal } from 'ncoded-component-library';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import LeadContext from '../../../providers/Lead/Lead.context';
import api from 'api';
import confirm from 'modules/confirm';
import DropzoneField from 'components/DropzoneField';
import validations from 'validations';
import UploadIcon from 'icons/Upload.icon';
import { FILE_TYPES } from 'router/subrouters/DataRoom/components/DataRoomDocumentsRouter/data/DataTypes';

import './Nbio.styles.scss';

type NbioProps = {
  variant?: 'outline' | 'solid' | 'link';
  buttonContent?: string;
};

const Nbio: React.FC<NbioProps> = ({ variant = 'outline', buttonContent }) => {
  const [isLoading, setIsLoading] = useState(false);

  const { popServerError, popSuccess } = useContext(PopNotificationsContext);
  const {
    leadId,
    updateLead,
    enquiry: { _id: enquiryId },
  } = useContext(LeadContext);

  const [modal, modalRef] = useCallbackRef();

  const { t } = useTranslation();

  const uploadNBIO = useCallback(
    async (file, _, config) => {
      const data = await api.portfolio.buyer.uploadNBIODocument(
        file,
        leadId,
        config,
      );

      return data;
    },
    [leadId],
  );

  const deletedNBIO = useCallback(
    async (file) => {
      const {
        data: { fileId },
      } = await api.portfolio.buyer.deleteNBIODocument(leadId, file.id);
      return fileId;
    },
    [leadId],
  );

  const onNBIOSubmit = useCallback(
    async (values: Record<any, any>) => {
      if (
        await confirm(
          {
            title: t('submitNBIO'),
            content: t('submitNBIOMessage'),
          },
          true,
        )
      ) {
        setIsLoading(true);

        try {
          const {
            data: {
              lead: { numberOfEnabledDocuments },
            },
          } = await api.portfolio.buyer.submitNBIO(leadId, values.NBIODocument);
          const {
            data: { lead },
          } = await api.portfolio.buyer.getBuyingPortfolio(enquiryId);

          popSuccess(
            t('successfullySubmitedNBIO', {
              count: numberOfEnabledDocuments,
            }),
          );

          updateLead(lead);
          modal?.close();
        } catch (e) {
          popServerError(e);
        } finally {
          setIsLoading(false);
        }
      }
    },
    [enquiryId, leadId, modal, popServerError, popSuccess, t, updateLead],
  );

  return (
    <>
      <Button variant={variant} icon={UploadIcon} onClick={modal?.open}>
        {buttonContent || t('submitNBIO')}
      </Button>
      <Modal
        ref={modalRef}
        title={t('uploadNBIODocument')}
        className="submit-nbio-modal"
      >
        {isLoading && <Loader />}
        <Form
          onSubmit={onNBIOSubmit}
          render={({ handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <Field
                name="NBIODocument"
                fileName=""
                component={DropzoneField}
                validate={validations.required()}
                uploadRequest={uploadNBIO}
                deleteRequest={deletedNBIO}
                maxFiles={1}
                accept={FILE_TYPES.PDF}
                showApplicationAsIframe
              />
              <Button type="submit">{t('submit')}</Button>
            </form>
          )}
        />
      </Modal>
    </>
  );
};

export default Nbio;
